import { createContext, useContext } from "react";
import type { ProcessSection, ProcessWorkflow } from "../../../api";
import type { FieldRef, Problem, Selection } from "../types";

/** What is being dragged between step cards right now. */
export type DragPayload =
  | { kind: "form"; id: number }
  | { kind: "workflow"; id: number }
  | null;

export type CanvasHandlers = {
  sections: Record<number, ProcessSection>;
  workflows: ProcessWorkflow[];
  fields: FieldRef[];
  problems: Problem[];
  selection: Selection;
  /** Stage key -> whether it runs, from the last simulation. Null when none. */
  simulation: Record<string, boolean> | null;
  onSelect: (selection: Selection) => void;
  /** Move a section or sign-off into a step (or out, with a null key). */
  onPlaceItem: (
    kind: "form" | "workflow",
    itemId: number,
    toStageKey: string | null,
    index: number
  ) => void;
  onAddStage: (kind: string, atIndex: number) => void;
  onRemoveStage: (key: string) => void;
  onAddWorkflow: (stageKey: string) => void;
  onCreateSection: (stageKey: string) => void;
  /** What the pointer is carrying, so drop targets can light up. */
  dragging: DragPayload;
  setDragging: (payload: DragPayload) => void;
};

/**
 * Handlers reach the nodes through context rather than `node.data`.
 *
 * React Flow re-creates every node object when the nodes array changes, and it
 * compares `data` by identity to decide what to re-render. Putting callbacks in
 * `data` therefore either re-renders every node on every keystroke, or -- if
 * the callbacks are memoised into it -- makes the memo dependency list the
 * thing that decides correctness. Context sidesteps both: `data` stays plain
 * serialisable description, and the handlers are looked up where they are used.
 */
export const CanvasContext = createContext<CanvasHandlers | null>(null);

export const useCanvas = (): CanvasHandlers => {
  const value = useContext(CanvasContext);
  if (!value) throw new Error("useCanvas used outside the process canvas");
  return value;
};
